/** MagnifierLens — portal-wide magnifying lens that follows the mouse.
 * Toggle from the floating button; the lens shows a 2x live view of the page. */
import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Search, X } from "lucide-react";

const LENS = 220;

export function MagnifierLens() {
  const [on, setOn] = useState(() => localStorage.getItem("pxn_magnifier") === "1");
  const [zoom, setZoom] = useState(() => Number(localStorage.getItem("pxn_magnifier_zoom") || 2));
  const lensRef = useRef(null);
  const wrapRef = useRef(null);
  const pos = useRef({ x: -9999, y: -9999 });

  useEffect(() => { localStorage.setItem("pxn_magnifier", on ? "1" : "0"); }, [on]);
  useEffect(() => { localStorage.setItem("pxn_magnifier_zoom", String(zoom)); }, [zoom]);

  useEffect(() => {
    if (!on) return undefined;
    let refreshTimer = null;

    const refreshClone = () => {
      const wrap = wrapRef.current;
      const root = document.getElementById("root");
      if (!wrap || !root) return;
      const clone = root.cloneNode(true);
      clone.querySelectorAll("[data-magnifier]").forEach((n) => n.remove());
      clone.querySelectorAll("script, iframe, video").forEach((n) => n.remove());
      const srcF = root.querySelectorAll("input, textarea, select");
      const dstF = clone.querySelectorAll("input, textarea, select");
      srcF.forEach((el, i) => {
        const d = dstF[i];
        if (!d) return;
        if (el.tagName === "TEXTAREA") d.textContent = el.value;
        else if (el.type === "checkbox" || el.type === "radio") { if (el.checked) d.setAttribute("checked", ""); else d.removeAttribute("checked"); }
        else d.setAttribute("value", el.value);
      });
      wrap.innerHTML = "";
      wrap.appendChild(clone);
      wrap.style.width = `${document.documentElement.scrollWidth}px`;
      const srcAll = root.querySelectorAll("*");
      const dstAll = clone.querySelectorAll("*");
      srcAll.forEach((el, i) => {
        if ((el.scrollTop || el.scrollLeft) && dstAll[i]) {
          dstAll[i].scrollTop = el.scrollTop;
          dstAll[i].scrollLeft = el.scrollLeft;
        }
      });
      apply();
    };

    const apply = () => {
      const lens = lensRef.current;
      const wrap = wrapRef.current;
      if (!lens || !wrap) return;
      const R = LENS / 2;
      const { x, y } = pos.current;
      lens.style.left = `${x - R}px`;
      lens.style.top = `${y - R}px`;
      lens.style.opacity = x < -1000 ? "0" : "1";
      const px = x + window.scrollX;
      const py = y + window.scrollY;
      wrap.style.transform = `translate(${R - px * zoom}px, ${R - py * zoom}px) scale(${zoom})`;
    };

    const onMove = (e) => { pos.current = { x: e.clientX, y: e.clientY }; apply(); };
    const onScroll = () => {
      apply();
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(refreshClone, 350);
    };
    refreshClone();
    const iv = setInterval(refreshClone, 1500);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      clearInterval(iv);
      clearTimeout(refreshTimer);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [on, zoom]);

  return (
    <div data-magnifier>
      <div className="fixed bottom-5 left-5 z-[95] flex items-center gap-1.5" data-magnifier>
        {on && (
          <>
            <button onClick={() => setZoom((z) => Math.max(1.5, z - 0.5))} data-testid="magnifier-zoom-out" title="Lens zoom out"
              className="rounded-full bg-white p-2 text-slate-600 shadow-lg ring-1 ring-slate-200 hover:text-[#0F2042]"><ZoomOut size={14} /></button>
            <span className="rounded-full bg-white px-2 py-1 text-[10px] font-bold text-slate-600 shadow-lg ring-1 ring-slate-200" data-testid="magnifier-zoom-value">{zoom}x</span>
            <button onClick={() => setZoom((z) => Math.min(4, z + 0.5))} data-testid="magnifier-zoom-in" title="Lens zoom in"
              className="rounded-full bg-white p-2 text-slate-600 shadow-lg ring-1 ring-slate-200 hover:text-[#0F2042]"><ZoomIn size={14} /></button>
          </>
        )}
        <button onClick={() => setOn(!on)} data-testid="magnifier-toggle"
          title={on ? "Turn off magnifier lens" : "Turn on magnifier lens — magnifies wherever your mouse points"}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold shadow-lg ring-1 transition ${
            on ? "bg-[#F97316] text-white ring-orange-300" : "bg-white text-slate-600 ring-slate-200 hover:text-[#0F2042]"}`}>
          {on ? <X size={13} /> : <Search size={13} />} {on ? "Lens on" : "Magnifier"}
        </button>
      </div>
      {on && (
        <div ref={lensRef} data-magnifier data-testid="magnifier-lens"
          className="pointer-events-none fixed z-[200] overflow-hidden rounded-full border-4 border-[#F97316] bg-white shadow-2xl"
          style={{ width: LENS, height: LENS, opacity: 0 }}>
          <div ref={wrapRef} style={{ transformOrigin: "0 0", position: "absolute", left: 0, top: 0 }} />
          <div className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-white/60" />
        </div>
      )}
    </div>
  );
}
